/**
 * ============================================================================
 * useCourtsData - TanStack Query hook for courts data fetching
 * ============================================================================
 *
 * Fetches venue and courts data with proper caching and error handling
 *
 * ============================================================================
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { vendorApi } from '../services/api';
import type { Court } from '../types/courts';

// Debug logging for courts data
const DEBUG_COURTS = false;
const logCourts = (action: string, details?: any) => {
  if (!DEBUG_COURTS) return;
  const timestamp = new Date().toISOString().split('T')[1].slice(0, 12);
  console.log(
    `%c[${timestamp}] 🏟️ COURTS ${action}`,
    action === 'FETCH' ? 'color: #ef4444; font-weight: bold' :
    action === 'CACHE_HIT' ? 'color: #22c55e; font-weight: bold' :
    'color: #8b5cf6',
    details || ''
  );
};

interface CourtsDataResponse {
  venue: {
    id: number;
    name: string;
    sports?: Array<{
      id: number;
      name: string;
      custom_hourly_rate?: number | null;
    }>;
    base_hourly_rate?: number;
  };
  courts: Court[];
}

/**
 * Hook to fetch courts data for a venue
 */
export function useCourtsData(venueId: number | undefined) {
  const queryClient = useQueryClient();
  const queryKey = ['courts', venueId];

  // Log cache state when component mounts
  const existingData = queryClient.getQueryData(queryKey);
  const queryState = queryClient.getQueryState(queryKey);

  logCourts('MOUNT', {
    queryKey,
    hasCachedData: !!existingData,
    cacheState: queryState?.status,
    dataUpdatedAt: queryState?.dataUpdatedAt ? new Date(queryState.dataUpdatedAt).toISOString() : 'never',
  });

  return useQuery<CourtsDataResponse>({
    queryKey,
    queryFn: async () => {
      if (!venueId) {
        throw new Error('No venue ID provided');
      }

      const startTime = performance.now();
      logCourts('FETCH', { queryKey, reason: 'No cache or cache is stale' });

      // Fetch courts data
      const courts = await vendorApi.getCourts(venueId);

      const duration = (performance.now() - startTime).toFixed(1);
      logCourts('FETCHED', { queryKey, duration: `${duration}ms`, courtsCount: courts?.length });

      // Get venue data from user context (passed as venueId means we have it)
      // In real implementation, this would come from auth context
      // For now, we'll structure the response to match what the component expects

      return {
        venue: {
          id: venueId,
          name: 'Venue', // This will be replaced by actual venue data from context
          sports: [], // This will be populated from venue context
        },
        courts,
      };
    },
    enabled: !!venueId,
    staleTime: 2 * 60 * 1000, // 2 minutes - reasonable freshness for courts
    gcTime: 10 * 60 * 1000, // 10 minutes in cache
    refetchOnMount: true, // Refetch on mount if data is stale
    refetchOnWindowFocus: false, // Don't refetch when user returns to tab (too aggressive)
    placeholderData: (previousData) => previousData, // Keep showing old data while refetching
  });
}
